import React, { useEffect, useRef, useState } from 'react';
import * as pdfjsLib from 'pdfjs-dist';

// PDF.js worker 설정 (unpkg CDN 사용 - 버전 명시)
// pdfjsLib.version이 없을 경우를 대비해 하드코딩된 버전도 준비
const PDFJS_VERSION = pdfjsLib.version || '3.11.174';
pdfjsLib.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${PDFJS_VERSION}/build/pdf.worker.min.js`;

/**
 * Canvas 기반 PDF 뷰어
 * - iframe 대신 Canvas로 직접 렌더링
 * - 바운딩 박스 오버레이 가능
 */
export default function CanvasPDFViewer({
  file,
  pageNumber,
  onLoadSuccess,
  onPageDimensionsChange, // ⭐ 페이지 크기 변경 콜백
  highlightedField = null, // ⭐ 하이라이트할 필드
  boundingBoxes = [], // ⭐ 바운딩 박스 데이터
  fieldValueMap = {}, // ⭐ 필드-값 매핑
  scale: externalScale = null, // ⭐ 외부 스케일 제어 (Optional)
  groundingCoordinates = null, // ⭐ [Grounding] 직접 좌표 (LangExtract용) [x1, y1, x2, y2]
  pdfDimensions = null // ⭐ [Grounding] PDF 원본 크기 (스케일링용)
}) {
  const canvasRef = useRef(null);
  const highlightCanvasRef = useRef(null); // ⭐ 하이라이트 전용 Canvas
  const containerRef = useRef(null);
  const [pdfDoc, setPdfDoc] = useState(null);
  const [rendering, setRendering] = useState(false);
  const [internalScale, setInternalScale] = useState(1.5); // 내부 스케일 (기본값)

  // 외부 스케일이 있으면 그것을 사용, 아니면 내부 스케일 사용
  const scale = externalScale || internalScale;

  const [canvasDimensions, setCanvasDimensions] = useState({ width: 0, height: 0 }); // ⭐ Canvas 크기
  const [error, setError] = useState(null); // ⭐ 에러 상태 추가

  // PDF 문서 로드
  useEffect(() => {
    let isMounted = true;
    let objectUrl = null;
    setError(null); // 에러 초기화

    const loadPDF = async () => {
      try {
        console.log('📄 PDF 로딩 시작:', file);
        if (!file) {
          throw new Error('파일이 없습니다.');
        }

        // File 객체인 경우 Blob URL 생성
        let documentSource = file;
        if (file instanceof File) {
          objectUrl = URL.createObjectURL(file);
          documentSource = objectUrl;
        }

        const loadingTask = pdfjsLib.getDocument(documentSource);
        const pdf = await loadingTask.promise;

        if (isMounted) {
          setPdfDoc(pdf);
          console.log(`✅ PDF 로드 완료: ${pdf.numPages} 페이지`);
          if (onLoadSuccess) {
            onLoadSuccess({ numPages: pdf.numPages });
          }
        }
      } catch (error) {
        console.error('❌ PDF 로드 실패:', error);
        if (isMounted) {
          setError(error.message || 'PDF 파일을 불러오는데 실패했습니다.');
        }
      }
    };

    loadPDF();

    return () => {
      isMounted = false;
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
      }
    };
  }, [file, onLoadSuccess]);

  // 페이지 렌더링
  useEffect(() => {
    if (!pdfDoc || !canvasRef.current || rendering) return;

    const renderPage = async () => {
      setRendering(true);
      try {
        const page = await pdfDoc.getPage(pageNumber);
        const canvas = canvasRef.current;
        const context = canvas.getContext('2d');

        // 뷰포트 계산
        const viewport = page.getViewport({ scale });

        // Canvas 크기 설정
        canvas.width = viewport.width;
        canvas.height = viewport.height;

        // 렌더링
        const renderContext = {
          canvasContext: context,
          viewport: viewport
        };

        await page.render(renderContext).promise;

        console.log(`✅ 페이지 ${pageNumber} 렌더링 완료 (${viewport.width}x${viewport.height})`);

        // ⭐ Canvas 크기 저장
        setCanvasDimensions({ width: viewport.width, height: viewport.height });

        // ⭐ 하이라이트 Canvas 크기 동기화
        if (highlightCanvasRef.current) {
          highlightCanvasRef.current.width = viewport.width;
          highlightCanvasRef.current.height = viewport.height;
        }

        // ⭐ 페이지 크기 변경 알림
        if (onPageDimensionsChange) {
          onPageDimensionsChange({
            width: viewport.width,
            height: viewport.height,
            pageNumber
          });
        }
      } catch (error) {
        console.error('❌ 페이지 렌더링 실패:', error);
      } finally {
        setRendering(false);
      }
    };

    renderPage();
  }, [pdfDoc, pageNumber, scale, rendering, onPageDimensionsChange]);

  // ⭐ 하이라이트 렌더링 (Grounding 좌표 우선, 없으면 텍스트 매칭)
  useEffect(() => {
    if (!highlightCanvasRef.current || !canvasDimensions.width) return;

    const canvas = highlightCanvasRef.current;
    const context = canvas.getContext('2d');

    // 이전 하이라이트 지우기
    context.clearRect(0, 0, canvas.width, canvas.height);

    // ⭐ [Grounding] 직접 좌표가 있으면 우선 사용
    if (groundingCoordinates && groundingCoordinates.length === 4) {
      const [x1, y1, x2, y2] = groundingCoordinates;

      // 디버그 로깅
      console.log(`🎯 [Grounding Debug] Input coordinates: [${x1.toFixed(1)}, ${y1.toFixed(1)}, ${x2.toFixed(1)}, ${y2.toFixed(1)}]`);
      console.log(`🎯 [Grounding Debug] pdfDimensions: ${JSON.stringify(pdfDimensions)}`);
      console.log(`🎯 [Grounding Debug] canvasDimensions: ${JSON.stringify(canvasDimensions)}`);
      console.log(`🎯 [Grounding Debug] scale: ${scale}`);

      // PDF 좌표 → Canvas 좌표 스케일링
      let scaledX = x1 * scale;
      let scaledY = y1 * scale;
      let scaledWidth = (x2 - x1) * scale;
      let scaledHeight = (y2 - y1) * scale;

      // PDF 원본 크기가 있으면 좌표 스케일링
      if (pdfDimensions && pdfDimensions.width && pdfDimensions.height) {
        const pdfScaleX = canvasDimensions.width / pdfDimensions.width;
        const pdfScaleY = canvasDimensions.height / pdfDimensions.height;
        console.log(`🎯 [Grounding Debug] PDF→Canvas scale: X=${pdfScaleX.toFixed(3)}, Y=${pdfScaleY.toFixed(3)}`);
        scaledX = x1 * pdfScaleX;
        scaledY = y1 * pdfScaleY;
        scaledWidth = (x2 - x1) * pdfScaleX;
        scaledHeight = (y2 - y1) * pdfScaleY;
      } else {
        console.log(`⚠️ [Grounding Debug] pdfDimensions missing! Using basic scale only.`);
      }

      // 노란색 반투명 배경
      context.fillStyle = 'rgba(255, 215, 0, 0.4)';
      context.fillRect(scaledX, scaledY, scaledWidth, scaledHeight);

      // 노란색 테두리 (두껍게)
      context.strokeStyle = 'rgba(255, 165, 0, 1)';
      context.lineWidth = 3;
      context.strokeRect(scaledX, scaledY, scaledWidth, scaledHeight);

      console.log(`🎯 [Grounding] 하이라이트: [${scaledX.toFixed(0)}, ${scaledY.toFixed(0)}, ${scaledWidth.toFixed(0)}, ${scaledHeight.toFixed(0)}]`);
      return;
    }

    // ⭐ 텍스트 매칭 방식 (PaddleOCR용)
    if (!highlightedField || !boundingBoxes.length) return;

    // 매칭되는 바운딩 박스 찾기
    const fieldValue = fieldValueMap[highlightedField];
    if (!fieldValue) return;

    const matchedBoxes = boundingBoxes.filter(box => {
      const boxText = box.text?.trim().replace(/\s+/g, '');
      const fieldText = String(fieldValue).trim().replace(/\s+/g, '');

      return boxText === fieldText ||
        boxText.includes(fieldText) ||
        fieldText.includes(boxText);
    });

    if (matchedBoxes.length === 0) {
      console.log(`⚠️ 매칭되는 바운딩 박스 없음: ${highlightedField} = ${fieldValue}`);
      return;
    }

    // 하이라이트 그리기
    matchedBoxes.forEach((box, index) => {
      const [x1, y1, x2, y2] = box.box;

      // 좌표 스케일링
      const scaledX = x1 * scale;
      const scaledY = y1 * scale;
      const scaledWidth = (x2 - x1) * scale;
      const scaledHeight = (y2 - y1) * scale;

      // 노란색 반투명 배경
      context.fillStyle = 'rgba(255, 215, 0, 0.4)';
      context.fillRect(scaledX, scaledY, scaledWidth, scaledHeight);

      // 노란색 테두리 (애니메이션 효과)
      context.strokeStyle = 'rgba(255, 215, 0, 0.9)';
      context.lineWidth = 3;
      context.strokeRect(scaledX, scaledY, scaledWidth, scaledHeight);

      console.log(`🎯 하이라이트 ${index + 1}: [${scaledX.toFixed(0)}, ${scaledY.toFixed(0)}, ${scaledWidth.toFixed(0)}, ${scaledHeight.toFixed(0)}]`);
    });

    console.log(`✅ 하이라이트 렌더링 완료: ${highlightedField} = ${fieldValue} (${matchedBoxes.length}개 박스)`);
  }, [highlightedField, boundingBoxes, fieldValueMap, scale, canvasDimensions, groundingCoordinates, pdfDimensions]);

  // 줌 인/아웃
  const handleZoomIn = () => setInternalScale(prev => Math.min(prev + 0.25, 3));
  const handleZoomOut = () => setInternalScale(prev => Math.max(prev - 0.25, 0.5));
  const handleResetZoom = () => setInternalScale(1.5);

  return (
    <div className="relative w-full h-full bg-gray-100 overflow-hidden">
      {/* 줌 컨트롤 (우측 하단, 고정 위치) */}
      <div className="absolute bottom-4 right-4 z-50 flex gap-1 bg-white/90 backdrop-blur-sm rounded-md shadow-sm border border-gray-200 p-1 transition-opacity hover:opacity-100 opacity-60">
        <button
          onClick={handleZoomOut}
          className="w-6 h-6 flex items-center justify-center text-xs font-medium text-gray-600 hover:bg-gray-100 rounded"
          title="축소"
        >
          −
        </button>
        <button
          onClick={handleResetZoom}
          className="px-2 h-6 flex items-center justify-center text-[10px] font-medium text-gray-600 hover:bg-gray-100 rounded min-w-[40px]"
          title="초기화 (150%)"
        >
          {Math.round(scale * 100)}%
        </button>
        <button
          onClick={handleZoomIn}
          className="w-6 h-6 flex items-center justify-center text-xs font-medium text-gray-600 hover:bg-gray-100 rounded"
          title="확대"
        >
          +
        </button>
      </div>

      {/* 에러 메시지 표시 */}
      {error && (
        <div className="absolute inset-0 flex items-center justify-center bg-gray-100 z-20">
          <div className="bg-white p-4 rounded-lg shadow-lg max-w-sm text-center">
            <div className="text-red-500 mb-2">⚠️ PDF 로드 오류</div>
            <p className="text-sm text-gray-600">{error}</p>
            <p className="text-xs text-gray-400 mt-2">Worker: {pdfjsLib.GlobalWorkerOptions.workerSrc}</p>
          </div>
        </div>
      )}

      {/* 스크롤 가능한 영역 */}
      <div ref={containerRef} className="w-full h-full overflow-auto flex items-start justify-center p-4">
        <div className="relative">
          {/* PDF 렌더링 Canvas */}
          <canvas
            ref={canvasRef}
            className="shadow-lg"
            style={{ maxWidth: 'none' }}
          />
          {/* 하이라이트 오버레이 Canvas */}
          <canvas
            ref={highlightCanvasRef}
            className="absolute top-0 left-0 pointer-events-none"
            style={{
              maxWidth: 'none',
              mixBlendMode: 'multiply'
            }}
          />
        </div>
      </div>
    </div>
  );
}

